import { createContext, useContext, useState } from 'react';

import { useWindowWidth } from '@hooks/useWindowWidth';

import { cn } from '@utils/cn';

import { Button, Drawer, Icon, Popover, Text } from '@ui-component-library';

import type { PopOverItemProps, PopoverMenuProps } from './PopoverMenu.types';

const CloseContext = createContext<(() => void) | null>(null);

export const PopOverItem = ({
  icon,
  label,
  onClick,
  variant = 'default',
  isPlus = false
}: PopOverItemProps) => {
  const close = useContext(CloseContext);
  const isDanger = variant === 'danger';

  return (
    <div
      role="button"
      tabIndex={0}
      className="hover:bg-cloud-200 flex cursor-pointer items-center gap-2 rounded-lg p-2 transition-colors"
      onClick={() => {
        close?.();
        onClick?.();
      }}
    >
      <Icon name={icon} size="large" className={cn(isDanger && 'fill-[#C63A35]')} />
      <Text variant="text-sm/leading-none/normal" className={cn(isDanger && 'text-[#C63A35]')}>
        {label}
      </Text>
      {isPlus && (
        <Text
          variant="text-xs/leading-normal/medium"
          className="rounded-lg border border-solid border-transparent bg-[#F6F5FE] p-1 text-[#6856C3]"
        >
          Plus
        </Text>
      )}
    </div>
  );
};

export const PopoverMenu = ({
  icon,
  title,
  subtitle,
  children,
  disabled = false,
  className,
  trigger,
  onOpenChange
}: PopoverMenuProps) => {
  const [open, setOpen] = useState(false);
  const { isSmall } = useWindowWidth();

  const handleOpenChange = (value: boolean) => {
    setOpen(value);
    onOpenChange?.(value);
  };

  const close = () => handleOpenChange(false);

  if (isSmall) {
    const triggerElement = trigger ? (
      <div role="button" onClick={() => !disabled && handleOpenChange(true)}>
        {trigger}
      </div>
    ) : (
      <Button variant="tertiary" size="icon" onClick={() => !disabled && handleOpenChange(true)}>
        <Icon name={icon} className="color-charcoal-500" size="large" />
      </Button>
    );

    return (
      <div className={cn(className)}>
        {triggerElement}
        <Drawer isOpen={open} onClose={close}>
          <Drawer.Header className="items-center">
            <div className="flex flex-col">
              <Text variant="text-base/leading-normal/medium">{title}</Text>
              {subtitle && (
                <Text variant="text-xs/leading-normal/medium" className="text-[#737373]">
                  {subtitle}
                </Text>
              )}
            </div>
            <Button variant="tertiary" size="icon" onClick={close}>
              <Icon name="CrossSimple" className="color-charcoal-500" size="large" />
            </Button>
          </Drawer.Header>
          <Drawer.Content className="p-2">
            <CloseContext.Provider value={close}>{children}</CloseContext.Provider>
          </Drawer.Content>
          <Drawer.Footer className="flex justify-end">
            <Button variant="primary" onClick={close}>
              Avbryta
            </Button>
          </Drawer.Footer>
        </Drawer>
      </div>
    );
  }

  const desktopTrigger = trigger ?? (
    <Button variant="tertiary" size="icon">
      <Icon name={icon} className="color-charcoal-500" size="large" />
    </Button>
  );

  return (
    <Popover
      side="bottom"
      align="end"
      open={open}
      onOpenChange={handleOpenChange}
      className={cn('group cursor-pointer', className)}
      disabled={disabled}
      content={<CloseContext.Provider value={close}>{children}</CloseContext.Provider>}
    >
      {desktopTrigger}
    </Popover>
  );
};
